import React, { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Backdrop
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArticleIcon from '@mui/icons-material/Article';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadIcon from '@mui/icons-material/Upload';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Import drag and drop functionality
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';


// Sortable Tree Node Component (with disabled visual movement)
const SortableTreeNode = ({ 
  item, 
  level, 
  expandedNodes, 
  selectedNode, 
  onNodeSelect, 
  onToggleNode, 
  onContextMenu, 
  onEdit, 
  getContentIcon,
  isDragOverlay = false,
  dragOverInfo = null
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ 
    id: item.itemId,
    disabled: isDragOverlay,
  });

  const style = {
    // Completely disable all transforms and transitions during drag
    transform: 'none',
    transition: 'none',
    opacity: isDragging ? 0.3 : 1, // More transparent when dragging, but don't move
    zIndex: isDragging ? 999 : 'auto',
  };

  const isExpanded = expandedNodes.includes(item.itemId);
  const hasChildren = item.children && item.children.length > 0;
  const isContent = item.type === 'content';
  const isSelected = selectedNode?.itemId === item.itemId;

  const showDropIndicator = dragOverInfo && dragOverInfo.itemId === item.itemId;
  const showInsideIndicator = showDropIndicator && dragOverInfo.position === 'inside';

  return (
    <Box ref={setNodeRef} style={style}>
      {/* Drop Indicator - Before */}
      {showDropIndicator && dragOverInfo.position === 'before' && !showInsideIndicator && (
        <Box
          sx={{
            height: '3px',
            backgroundColor: '#2196f3',
            marginLeft: level * 1.5 + 'rem',
            marginRight: '1rem',
            borderRadius: '2px',
            boxShadow: '0 0 8px rgba(33, 150, 243, 0.8)',
            position: 'relative',
            zIndex: 2000, // Higher than drag overlay
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-6px',
              top: '-3px',
              width: '9px',
              height: '9px',
              backgroundColor: '#2196f3',
              borderRadius: '50%',
              boxShadow: '0 0 6px rgba(33, 150, 243, 0.8)',
            }
          }}
        />
      )}
      
              <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0.25,
            pl: level * 1.5,
            cursor: 'pointer',
            backgroundColor: showInsideIndicator 
              ? 'rgba(33, 150, 243, 0.1)' 
              : isSelected 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'transparent',
            border: showInsideIndicator 
              ? '2px dashed rgba(33, 150, 243, 0.6)' 
              : 'none',
            borderRadius: showInsideIndicator ? '4px' : '0',
            '&:hover': {
              backgroundColor: showInsideIndicator 
                ? 'rgba(33, 150, 243, 0.15)' 
                : isSelected 
                  ? 'rgba(255, 255, 255, 0.2)' 
                  : 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={() => onNodeSelect(item)}
          onContextMenu={(e) => onContextMenu(e, item)}
          onDoubleClick={() => {
            onNodeSelect(item);
            onEdit();
          }}
        >
        {/* Drag Handle */}
        {!isDragOverlay && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'grab',
              mr: 0.5,
              '&:active': {
                cursor: 'grabbing'
              }
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 14, color: '#6b7280', opacity: 0.7 }} />
          </Box>
        )}
        
        {/* Spacer for drag overlay */}
        {isDragOverlay && (
          <Box sx={{ width: 20, mr: 0.5 }} />
        )}

                  {/* Expand/Collapse Icon */}
          {hasChildren && !isDragOverlay && (
            <IconButton
              size="small"
              sx={{ 
                p: 0.25, 
                mr: 0.5,
                color: '#9ca3af',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleNode(item.itemId);
              }}
            >
              {isExpanded ? (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          )}
          
          {/* Spacer for items without children or drag overlay */}
          {(!hasChildren || isDragOverlay) && (
            <Box sx={{ width: 20, mr: 0.5 }} />
          )}

        {/* Icon */}
        <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
          {isContent ? (
            getContentIcon(item.contentType)
          ) : (
            isExpanded ? (
              <FolderOpenIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            ) : (
              <FolderIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            )
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: isContent ? '#d1d5db' : '#f3f4f6',
            fontWeight: isContent ? 400 : 500,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {item.title}
        </Typography>
      </Box>

      {/* Drop Indicator - After */}
      {showDropIndicator && dragOverInfo.position === 'after' && !showInsideIndicator && (
        <Box
          sx={{
            height: '3px',
            backgroundColor: '#2196f3',
            marginLeft: level * 1.5 + 'rem',
            marginRight: '1rem',
            borderRadius: '2px',
            boxShadow: '0 0 8px rgba(33, 150, 243, 0.8)',
            position: 'relative',
            zIndex: 2000, // Higher than drag overlay
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-6px',
              top: '-3px',
              width: '9px',
              height: '9px',
              backgroundColor: '#2196f3',
              borderRadius: '50%',
              boxShadow: '0 0 6px rgba(33, 150, 243, 0.8)',
            }
          }}
        />
      )}

      {/* Children */}
      {hasChildren && isExpanded && (
        <SortableTreeNodeList
          items={item.children}
          level={level + 1}
          expandedNodes={expandedNodes}
          selectedNode={selectedNode}
          onNodeSelect={onNodeSelect}
          onToggleNode={onToggleNode}
          onContextMenu={onContextMenu}
          onEdit={onEdit}
          getContentIcon={getContentIcon}
          dragOverInfo={dragOverInfo}
        />
      )}
    </Box>
  );
};

// Sortable Tree Node List Component
const SortableTreeNodeList = ({ 
  items, 
  level, 
  expandedNodes, 
  selectedNode, 
  onNodeSelect, 
  onToggleNode, 
  onContextMenu, 
  onEdit, 
  getContentIcon,
  dragOverInfo = null
}) => {
  const sortedItems = items
    .sort((a, b) => {
      // Sort modules first, then content
      if (a.type !== b.type) {
        return a.type === 'module' ? -1 : 1;
      }
      return (a.order || 0) - (b.order || 0);
    });

  return (
    <Box>
      {sortedItems.map(item => (
        <SortableTreeNode
          key={item.itemId}
          item={item}
          level={level}
          expandedNodes={expandedNodes}
          selectedNode={selectedNode}
          onNodeSelect={onNodeSelect}
          onToggleNode={onToggleNode}
          onContextMenu={onContextMenu}
          onEdit={onEdit}
          getContentIcon={getContentIcon}
          dragOverInfo={dragOverInfo}
        />
      ))}
    </Box>
  );
};

const ExplorerTree = ({ 
  tutorialStructure, 
  onContentSelect,
  onStructureUpdate 
}) => {
  const [expandedNodes, setExpandedNodes] = useState(['root']);
  const [selectedNode, setSelectedNode] = useState(null);
  const [addMenu, setAddMenu] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [createSectionDialog, setCreateSectionDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [createContentDialog, setCreateContentDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [reuploadDialog, setReuploadDialog] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [dragOverInfo, setDragOverInfo] = useState(null); // { itemId, position: 'before' | 'after' }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    // Only select the node, don't open content automatically
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setDragOverInfo(null);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setDragOverInfo(null);
      return;
    }

    // Find the active and over items
    const findItemInStructure = (itemId, structure = tutorialStructure) => {
      for (const item of structure) {
        if (item.itemId === itemId) {
          return { item, parent: null };
        }
        if (item.children) {
          const found = findItemInStructure(itemId, item.children);
          if (found) {
            return found.parent === null ? { ...found, parent: item } : found;
          }
        }
      }
      return null;
    };

    const activeResult = findItemInStructure(active.id);
    const overResult = findItemInStructure(over.id);

    if (!activeResult || !overResult) {
      setDragOverInfo(null);
      return;
    }

    const activeItem = activeResult.item;
    const overItem = overResult.item;
    const overParent = overResult.parent;
    const overParentId = overParent ? overParent.itemId : 'root';

    // Apply same validation rules as in handleDragEnd
    const isDescendant = (parentId, childStructure = tutorialStructure) => {
      for (const item of childStructure) {
        if (item.itemId === parentId) return true;
        if (item.children && isDescendant(parentId, item.children)) return true;
      }
      return false;
    };

    if (activeItem.children && isDescendant(overItem.itemId, activeItem.children)) {
      setDragOverInfo(null);
      return;
    }

    if (activeItem.type === 'content' && overParentId === 'root') {
      setDragOverInfo(null);
      return;
    }

    // Determine drop position and type
    const activeParent = activeResult.parent;
    const activeParentId = activeParent ? activeParent.itemId : 'root';
    
    let position = 'before'; // Default
    let dropType = 'reorder'; // 'reorder' or 'insert'
    
    // Apply business rules first
    // 1. Content can only be placed in submodules, not top-level modules
    if (activeItem.type === 'content' && overItem.type === 'module' && !isSubmodule(overItem)) {
      setDragOverInfo(null);
      return;
    }
    
    // Note: We'll handle submodule nesting prevention in the "insert inside" logic below
    
    // 3. Submodules cannot be placed among content items
    if (activeItem.type === 'module' && overItem.type === 'content') {
      setDragOverInfo(null);
      return;
    }
    
    // 4. Top-level modules can only move among other top-level modules
    if (activeItem.type === 'module' && !isSubmodule(activeItem) && overParentId !== 'root') {
      setDragOverInfo(null);
      return;
    }
    
    // 5. Submodules cannot be moved to root level (cannot be alongside top-level modules)
    // But they can be inserted inside top-level modules
    if (activeItem.type === 'module' && isSubmodule(activeItem) && overParentId === 'root' && overItem.type !== 'module') {
      setDragOverInfo(null);
      return;
    }

    // Check if we're dropping content onto a module (to insert inside)
    if (activeItem.type === 'content' && overItem.type === 'module') {
      // Don't allow dropping content into its current parent
      if (activeParentId === overItem.itemId) {
        setDragOverInfo(null);
        return;
      }
      // Content being dropped on a module - insert inside
      dropType = 'insert';
      position = 'inside';
    } else if (activeItem.type === 'module' && overItem.type === 'module') {
      // Module being dropped on another module
      if (isSubmodule(activeItem) && isSubmodule(overItem)) {
        // Submodule to submodule - prevent nesting, only allow reordering
        dropType = 'reorder';
        if (activeParentId === overParentId) {
          // Same parent - reorder among siblings
          const siblings = overParent ? overParent.children : [];
          const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
          const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
          const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);
          
          if (activeIndex !== -1 && overIndex !== -1) {
            position = activeIndex < overIndex ? 'after' : 'before';
          }
        } else {
          // Different parent - reorder in new parent
          position = 'before'; // Default position
        }
      } else if (isSubmodule(activeItem) && !isSubmodule(overItem)) {
        // Submodule to top-level module - allow inserting inside
        if (activeParentId === overItem.itemId) {
          // Already in this parent, don't show indicator
          setDragOverInfo(null);
          return;
        }
        dropType = 'insert';
        position = 'inside';
      } else {
        // Other module combinations - use default logic
        if (activeParentId === overParentId) {
          // Same parent - determine based on current order
          const siblings = overParent ? overParent.children : tutorialStructure.filter(item => 
            item.type === 'module' && (!item.parentId || item.parentId === 'root')
          );
          
          const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
          const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
          const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);
          
          if (activeIndex !== -1 && overIndex !== -1) {
            position = activeIndex < overIndex ? 'after' : 'before';
          }
          dropType = 'reorder';
        } else {
          // Different parent - reorder
          dropType = 'reorder';
        }
      }
    } else if (activeParentId === overParentId) {
      // Same parent - determine based on current order
      const siblings = overParent ? overParent.children : tutorialStructure.filter(item => 
        item.type === 'module' && (!item.parentId || item.parentId === 'root')
      );
      
      const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
      const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
      const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);
      
      if (activeIndex !== -1 && overIndex !== -1) {
        position = activeIndex < overIndex ? 'after' : 'before';
      }
      dropType = 'reorder';
    } else {
      // Different parent - reorder
      dropType = 'reorder';
    }

    // Show drop indicator
    setDragOverInfo({
      itemId: over.id,
      position: position,
      dropType: dropType
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    // Find the active and over items
    const findItemInStructure = (itemId, structure = tutorialStructure) => {
      for (const item of structure) {
        if (item.itemId === itemId) {
          return { item, parent: null };
        }
        if (item.children) {
          const found = findItemInStructure(itemId, item.children);
          if (found) {
            return found.parent === null ? { ...found, parent: item } : found;
          }
        }
      }
      return null;
    };

    const activeResult = findItemInStructure(active.id);
    const overResult = findItemInStructure(over.id);

    if (!activeResult || !overResult) {
      setActiveId(null);
      return;
    }

    const activeItem = activeResult.item;
    const activeParent = activeResult.parent;
    const overItem = overResult.item;
    const overParent = overResult.parent;

    // Prevent invalid drag operations
    // 1. Can't drag a parent into its own child
    const isDescendant = (parentId, childStructure = tutorialStructure) => {
      for (const item of childStructure) {
        if (item.itemId === parentId) return true;
        if (item.children && isDescendant(parentId, item.children)) return true;
      }
      return false;
    };

    if (activeItem.children && isDescendant(overItem.itemId, activeItem.children)) {
      setActiveId(null);
      return;
    }

    // 2. Only allow certain drag combinations
    // - Modules can be dragged to root or other modules
    // - Content can be dragged to modules only
    const activeParentId = activeParent ? activeParent.itemId : 'root';
    const overParentId = overParent ? overParent.itemId : 'root';

    if (activeItem.type === 'content' && overParentId === 'root') {
      // Content cannot be placed at root level
      setActiveId(null);
      return;
    }
    
    // Apply business rules
    // 1. Content can only be placed in submodules, not top-level modules
    if (activeItem.type === 'content' && overItem.type === 'module' && !isSubmodule(overItem)) {
      setActiveId(null);
      return;
    }
    
    // Note: We'll handle submodule nesting prevention in the "insert inside" logic below
    
    // 3. Submodules cannot be placed among content items
    if (activeItem.type === 'module' && overItem.type === 'content') {
      setActiveId(null);
      return;
    }
    
    // 4. Top-level modules can only move among other top-level modules
    if (activeItem.type === 'module' && !isSubmodule(activeItem) && overParentId !== 'root') {
      setActiveId(null);
      return;
    }
    
    // 5. Submodules cannot be moved to root level (cannot be alongside top-level modules)
    // But they can be inserted inside top-level modules
    if (activeItem.type === 'module' && isSubmodule(activeItem) && overParentId === 'root' && overItem.type !== 'module') {
      setActiveId(null);
      return;
    }

    let newOrder;
    let targetParentId = overParentId;
    
    // Check if this is an "insert inside" operation
    if (activeItem.type === 'content' && overItem.type === 'module') {
      // Don't allow dropping content into its current parent
      if (activeParentId === overItem.itemId) {
        setActiveId(null);
        return;
      }
      
      // Only allow dropping content into submodules (not top-level modules)
      if (!isSubmodule(overItem)) {
        setActiveId(null);
        return;
      }
      
      // Insert content inside the target submodule
      targetParentId = overItem.itemId;
      const targetModuleChildren = overItem.children || [];
      newOrder = targetModuleChildren.length + 1; // Add to end
    } else if (activeItem.type === 'module' && overItem.type === 'module') {
      // Module to module operations
      if (isSubmodule(activeItem) && !isSubmodule(overItem)) {
        // Submodule to top-level module - check if this is an insert operation
        const currentDragInfo = dragOverInfo;
        if (currentDragInfo && currentDragInfo.dropType === 'insert') {
          // Insert submodule inside the target top-level module
          if (activeParentId === overItem.itemId) {
            setActiveId(null);
            return;
          }
          targetParentId = overItem.itemId;
          const targetModuleChildren = overItem.children || [];
          newOrder = targetModuleChildren.length + 1; // Add to end
        } else {
          // This should not happen as we only allow insert for submodule to top-level module
          setActiveId(null);
          return;
        }
      } else if (isSubmodule(activeItem) && isSubmodule(overItem)) {
        // Submodule to submodule - handle reordering
        if (activeParentId === overParentId) {
          // Same parent: reorder within siblings
          const siblings = activeParent ? activeParent.children : [];
          const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
          const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
          const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);

          if (activeIndex === -1 || overIndex === -1) {
            setActiveId(null);
            return;
          }

          newOrder = overIndex + 1;
        } else {
          // Different parent: move to new parent
          const newParentSiblings = overParent ? overParent.children : [];
          const sortedNewSiblings = newParentSiblings.sort((a, b) => (a.order || 0) - (b.order || 0));
          const overIndex = sortedNewSiblings.findIndex(item => item.itemId === over.id);

          if (overIndex === -1) {
            setActiveId(null);
            return;
          }

          newOrder = overIndex + 1;
          targetParentId = overParentId;
        }
      } else if (!isSubmodule(activeItem) && !isSubmodule(overItem)) {
        // Top-level module to top-level module - handle reordering
        if (activeParentId === overParentId) {
          // Same parent (root): reorder within siblings
          const siblings = tutorialStructure.filter(item => 
            item.type === 'module' && (!item.parentId || item.parentId === 'root')
          );
          const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
          const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
          const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);

          if (activeIndex === -1 || overIndex === -1) {
            setActiveId(null);
            return;
          }

          newOrder = overIndex + 1;
        } else {
          // This shouldn't happen for top-level modules as they should always have root as parent
          setActiveId(null);
          return;
        }
      } else {
        // Other unhandled module combinations - should not happen
        setActiveId(null);
        return;
      }
    } else if (activeItem.type === 'content' && overItem.type === 'content') {
      // Content to content - handle reordering
      if (activeParentId === overParentId) {
        // Same parent: reorder within siblings
        const siblings = activeParent ? activeParent.children : [];
        const sortedSiblings = siblings.sort((a, b) => (a.order || 0) - (b.order || 0));
        const activeIndex = sortedSiblings.findIndex(item => item.itemId === active.id);
        const overIndex = sortedSiblings.findIndex(item => item.itemId === over.id);

        if (activeIndex === -1 || overIndex === -1) {
          setActiveId(null);
          return;
        }

        newOrder = overIndex + 1;
      } else {
        // Different parent: move content to new parent and position it relative to the target content
        const newParentSiblings = overParent ? overParent.children : [];
        const sortedNewSiblings = newParentSiblings.sort((a, b) => (a.order || 0) - (b.order || 0));
        const overIndex = sortedNewSiblings.findIndex(item => item.itemId === over.id);

        if (overIndex === -1) {
          setActiveId(null);
          return;
        }

        newOrder = overIndex + 1;
        targetParentId = overParentId;
      }
    } else {
      // Other combinations are not supported
      setActiveId(null);
      return;
    }

    // Ensure newOrder has been calculated
    if (typeof newOrder !== 'number' || isNaN(newOrder)) {
      console.error('Failed to calculate newOrder for reorder operation');
      setError('Failed to calculate new order position');
      setActiveId(null);
      setDragOverInfo(null);
      return;
    }

    try {
      // Call the structure update with reorder action
      await onStructureUpdate('reorder', {
        itemId: active.id,
        newParentId: targetParentId,
        newOrder: newOrder
      });

      console.log('Reorder item:', { itemId: active.id, newParentId: targetParentId, newOrder });
    } catch (error) {
      console.error('Failed to reorder item:', error);
      setError('Failed to reorder item');
    }

    setActiveId(null);
    setDragOverInfo(null);
  };

  const handleAddClick = (event) => {
    // If no node is selected but we're showing the add button (empty tutorial case), use root node
    const targetNode = selectedNode || rootNode;
    if (!targetNode) return;
    
    // If we're using rootNode as fallback, select it first
    if (!selectedNode && targetNode === rootNode) {
      setSelectedNode(rootNode);
    }
    
    setAddMenu({
      mouseX: event.currentTarget.getBoundingClientRect().left,
      mouseY: event.currentTarget.getBoundingClientRect().bottom + 4,
    });
  };

  const closeAddMenu = () => {
    setAddMenu(null);
  };

  const handleContextMenu = (event, node) => {
    event.preventDefault();
    setSelectedNode(node);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const handleEdit = () => {
    closeContextMenu();
    if (selectedNode) {
      setEditTitle(selectedNode.title || '');
      setEditDescription(selectedNode.description || '');
      setError('');
      setSuccessMessage('');
      setEditDialog(true);
    }
  };

  const handleDelete = () => {
    closeContextMenu();
    setDeleteConfirmDialog(true);
  };

  const handleRename = () => {
    closeContextMenu();
    if (selectedNode) {
      setEditTitle(selectedNode.title || '');
      // Only set description for content items, modules don't have descriptions in rename
      if (selectedNode.type === 'content') {
        setEditDescription(selectedNode.description || '');
      } else {
        setEditDescription('');
      }
      setError('');
      setEditDialog(true);
    }
  };

  const handleMoreClick = (event, node) => {
    event.stopPropagation();
    setSelectedNode(node);
    setContextMenu({
      mouseX: event.currentTarget.getBoundingClientRect().left,
      mouseY: event.currentTarget.getBoundingClientRect().bottom + 4,
    });
  };

  const handleReupload = () => {
    closeContextMenu();
    if (selectedNode && selectedNode.type === 'content') {
      // Set the correct upload type before showing the dialog
      setUploadType(selectedNode.contentType);
      setReuploadDialog(true);
    }
  };

  const isRootOrTopLevel = (node) => {
    if (!node) return false;
    return node.itemId === 'root' || (!node.parentId || node.parentId === 'root');
  };

  const isSubmodule = (node) => {
    if (!node || node.type === 'content') return false;
    return node.parentId && node.parentId !== 'root';
  };

  const handleCreateSection = () => {
    closeAddMenu();
    setSectionTitle('');
    setError('');
    setSuccessMessage('');
    setCreateSectionDialog(true);
  };

  const handleUploadContent = (contentType) => {
    closeAddMenu();
    setUploadType(contentType);
    setContentTitle('');
    setContentDescription('');
    setError('');
    setSuccessMessage('');
    setCreateContentDialog(true);
  };

  const handleUploadFile = () => {
    if (!contentTitle.trim()) {
      setError('Title is required');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleReuploadFile = () => {
    if (selectedNode && selectedNode.type === 'content') {
      // uploadType should already be set correctly in handleReupload
      fileInputRef.current?.click();
    }
  };

  const confirmCreateSection = async () => {
    if (!sectionTitle.trim()) {
      setError('Section title is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const newSection = {
        itemId: `section_${Date.now()}`,
        type: 'module',
        title: sectionTitle.trim(),
        parentId: selectedNode?.itemId || 'root',
        order: (selectedNode?.children?.length || 0) + 1,
        children: []
      };

      await onStructureUpdate('create', newSection);
      setCreateSectionDialog(false);
      setSectionTitle('');
      setSuccessMessage('Section created successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create section');
    } finally {
      setLoading(false);
    }
  };

  const confirmEdit = async () => {
    if (!editTitle.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const updateData = {
        itemId: selectedNode.itemId,
        type: selectedNode.type,
        title: editTitle.trim()
      };
      
      // Only include description for content items (modules don't have descriptions)
      if (selectedNode.type === 'content') {
        updateData.description = editDescription.trim();
      }
      
      await onStructureUpdate('edit', updateData);
      setEditDialog(false);
      setEditTitle('');
      setEditDescription('');
      const actionType = selectedNode.type === 'content' ? 'Content' : 'Section';
      setSuccessMessage(`${actionType} updated successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedNode) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await onStructureUpdate('delete', selectedNode);
      setDeleteConfirmDialog(false);
      const actionType = selectedNode.type === 'content' ? 'Content' : 'Section';
      setSuccessMessage(`${actionType} deleted successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSelectedNode(null);
    } catch (err) {
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (reuploadDialog) {
        // Re-upload existing content
        const reuploadContent = {
          ...selectedNode,
          file: file
        };

        await onStructureUpdate('reupload', reuploadContent);
        
        // Close dialog
        setReuploadDialog(false);
        setSuccessMessage('Content re-uploaded successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Create content with user-provided title and description
        const newContent = {
          itemId: `content_${Date.now()}`,
          type: 'content',
          title: contentTitle.trim(),
          description: contentDescription.trim(),
          contentType: uploadType,
          parentId: selectedNode?.itemId || 'root',
          order: (selectedNode?.children?.length || 0) + 1,
          file: file
        };

        await onStructureUpdate('upload', newContent);
        
        // Close dialog and reset form
        setCreateContentDialog(false);
        setContentTitle('');
        setContentDescription('');
        const contentTypeDisplay = uploadType === 'video' ? 'Video' : uploadType === 'pdf' ? 'PDF' : 'Markdown';
        setSuccessMessage(`${contentTypeDisplay} uploaded successfully`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError(reuploadDialog ? 'Failed to re-upload content' : 'Failed to upload content');
    } finally {
      setLoading(false);
    }

    // Reset file input and upload type
    event.target.value = '';
    setUploadType('');
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return <VideoLibraryIcon sx={{ fontSize: 16, color: '#9ca3af' }} />;
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 16, color: '#9ca3af' }} />;
      case 'text':
        return <DescriptionIcon sx={{ fontSize: 16, color: '#9ca3af' }} />;
      default:
        return <ArticleIcon sx={{ fontSize: 16, color: '#9ca3af' }} />;
    }
  };

  // Get active item for drag overlay
  const findActiveItem = (itemId, structure = tutorialStructure) => {
    for (const item of structure) {
      if (item.itemId === itemId) {
        return item;
      }
      if (item.children) {
        const found = findActiveItem(itemId, item.children);
        if (found) return found;
      }
    }
    return null;
  };
  
  const activeItem = activeId ? findActiveItem(activeId) : null;

  // Get top-level modules
  const topLevelModules = tutorialStructure.filter(item => 
    item.type === 'module' && (!item.parentId || item.parentId === 'root')
  );

  const rootNode = { itemId: 'root', type: 'module', title: 'NeuroMuse Tutorial', children: topLevelModules };
  const isRootSelected = selectedNode?.itemId === 'root';

  return (
    <Box onClick={(e) => {
      // Click outside to deselect
      if (e.target === e.currentTarget) {
        setSelectedNode(null);
      }
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.snackbar,
            minWidth: '300px'
          }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.snackbar,
            minWidth: '300px'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Root Node with Add Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          cursor: 'pointer',
          backgroundColor: isRootSelected ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
          '&:hover': {
            backgroundColor: isRootSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'
          }
        }}
        onClick={() => handleNodeSelect(rootNode)}
        onContextMenu={(e) => handleContextMenu(e, rootNode)}
      >
        <IconButton
          size="small"
          sx={{ 
            p: 0.25, 
            mr: 0.5,
            color: '#9ca3af',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleNode('root');
          }}
        >
          {expandedNodes.includes('root') ? (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>

        <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
          {expandedNodes.includes('root') ? (
            <FolderOpenIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
          ) : (
            <FolderIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: '#f3f4f6',
            fontWeight: 600,
            flex: 1
          }}
        >
          NeuroMuse Tutorial
        </Typography>

        {/* More options button - show for all selected nodes except root */}
        {selectedNode && selectedNode.itemId !== 'root' && (
          <IconButton
            size="small"
            onClick={(e) => handleMoreClick(e, selectedNode)}
            sx={{ 
              color: '#9ca3af',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            title="More options"
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}

        {/* Add button - show for root and module nodes, not for content. Always show for root when no modules exist */}
        {(selectedNode && (selectedNode.itemId === 'root' || selectedNode.type === 'module')) || 
         (topLevelModules.length === 0 && expandedNodes.includes('root')) ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleAddClick(e);
            }}
            sx={{ 
              color: '#9ca3af',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            title="Add"
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>

      {/* Tutorial Structure with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {expandedNodes.includes('root') && (
          <Box sx={{ ml: 1 }}>
            {topLevelModules.length > 0 ? (
              <SortableTreeNodeList
                items={topLevelModules}
                level={0}
                expandedNodes={expandedNodes}
                selectedNode={selectedNode}
                onNodeSelect={handleNodeSelect}
                onToggleNode={toggleNode}
                onContextMenu={handleContextMenu}
                onEdit={handleEdit}
                getContentIcon={getContentIcon}
                dragOverInfo={dragOverInfo}
              />
            ) : (
              <Box sx={{ 
                py: 3, 
                px: 2, 
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <Typography variant="body2" sx={{ 
                  fontSize: '12px',
                  mb: 1,
                  opacity: 0.8
                }}>
                  No tutorial modules found
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontSize: '11px',
                  opacity: 0.6
                }}>
                  Click the + button above to add your first section
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        <DragOverlay
          style={{
            zIndex: 1000, // Lower than drop indicators
          }}
        >
          {activeItem ? (
            <Box
              sx={{
                backgroundColor: '#2c2c2c',
                borderRadius: 1,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                border: '1px solid #404040',
                opacity: 0.7, // More transparent to see through
                pointerEvents: 'none' // Don't interfere with drop detection
              }}
            >
              <SortableTreeNode
                item={activeItem}
                level={0}
                expandedNodes={[]}
                selectedNode={null}
                onNodeSelect={() => {}}
                onToggleNode={() => {}}
                onContextMenu={() => {}}
                onEdit={() => {}}
                getContentIcon={getContentIcon}
                isDragOverlay={true}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={uploadType === 'video' ? '.mp4,.webm,.ogg' : uploadType === 'pdf' ? '.pdf' : '.md,.txt,.markdown'}
        onChange={handleFileSelect}
      />

      {/* Add Menu */}
      <Menu
        open={addMenu !== null}
        onClose={closeAddMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          addMenu !== null
            ? { top: addMenu.mouseY, left: addMenu.mouseX }
            : undefined
        }
      >
        {isRootOrTopLevel(selectedNode) ? (
          // For root or top-level modules, only show "Add a new section"
          <MenuItem onClick={handleCreateSection}>
            <FolderIcon sx={{ mr: 1, fontSize: 16 }} />
            Add a new section
          </MenuItem>
        ) : isSubmodule(selectedNode) ? (
          // For submodules, show three content options
          [
            <MenuItem key="video" onClick={() => handleUploadContent('video')}>
              <VideoLibraryIcon sx={{ mr: 1, fontSize: 16 }} />
              Add Video
            </MenuItem>,
            <MenuItem key="pdf" onClick={() => handleUploadContent('pdf')}>
              <PictureAsPdfIcon sx={{ mr: 1, fontSize: 16 }} />
              Add PDF
            </MenuItem>,
            <MenuItem key="markdown" onClick={() => handleUploadContent('text')}>
              <DescriptionIcon sx={{ mr: 1, fontSize: 16 }} />
              Add Markdown
            </MenuItem>
          ]
                 ) : null}
      </Menu>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {selectedNode && selectedNode.itemId !== 'root' && (
          selectedNode.type === 'content' ? [
            // For content items: Edit, Re-upload, and Delete
            <MenuItem key="edit" onClick={handleEdit}>
              <EditIcon sx={{ mr: 1, fontSize: 16 }} />
              Edit
            </MenuItem>,
            <MenuItem key="reupload" onClick={handleReupload}>
              <UploadIcon sx={{ mr: 1, fontSize: 16 }} />
              Re-upload
            </MenuItem>,
            <MenuItem key="delete" onClick={handleDelete} sx={{ color: '#ef4444' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Delete
            </MenuItem>
          ] : [
            // For module items: Rename and Delete
            <MenuItem key="rename" onClick={handleRename}>
              <DriveFileRenameOutlineIcon sx={{ mr: 1, fontSize: 16 }} />
              Rename
            </MenuItem>,
            <MenuItem key="delete" onClick={handleDelete} sx={{ color: '#ef4444' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Delete
            </MenuItem>
          ]
        )}
      </Menu>

      {/* Create Section Dialog */}
      <Dialog open={createSectionDialog} onClose={() => setCreateSectionDialog(false)}>
        <DialogTitle>Create New Section</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Section Title"
            fullWidth
            variant="outlined"
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                confirmCreateSection();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSectionDialog(false)} disabled={loading}>Cancel</Button>
          <Button onClick={confirmCreateSection} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Content Dialog */}
      <Dialog open={createContentDialog} onClose={() => setCreateContentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add {uploadType === 'video' ? 'Video' : uploadType === 'pdf' ? 'PDF' : 'Markdown'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={contentTitle}
            onChange={(e) => {
              setContentTitle(e.target.value);
              setError('');
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={contentDescription}
            onChange={(e) => setContentDescription(e.target.value)}
            placeholder="Optional description for this content"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateContentDialog(false);
            setContentTitle('');
            setContentDescription('');
            setError('');
            setUploadType('');
          }} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleUploadFile} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedNode?.type === 'content' ? 'Edit Content' : 'Rename Section'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={editTitle}
            onChange={(e) => {
              setEditTitle(e.target.value);
              setError('');
            }}
            sx={{ mb: selectedNode?.type === 'content' ? 2 : 0 }}
          />
          {/* Only show description field for content items */}
          {selectedNode?.type === 'content' && (
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Optional description"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialog(false);
            setEditTitle('');
            setEditDescription('');
            setError('');
          }} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={confirmEdit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-upload Confirmation Dialog */}
      <Dialog open={reuploadDialog} onClose={() => setReuploadDialog(false)}>
        <DialogTitle>Re-upload Content</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Do you want to replace the file for "{selectedNode?.title}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current file type: {selectedNode?.contentType === 'video' ? 'Video' : selectedNode?.contentType === 'pdf' ? 'PDF' : 'Markdown'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReuploadDialog(false);
            setError('');
          }} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleReuploadFile} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Select New File'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedNode?.title}"? 
            {selectedNode?.type === 'module' && ' This will also delete all contents within this section.'}
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            确定要删除 "{selectedNode?.title}" 吗？
            {selectedNode?.type === 'module' && ' 这将同时删除该部分中的所有内容。'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)} disabled={loading}>Cancel / 取消</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete / 删除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExplorerTree;